import * as THREE from 'three';

/**
 * Particle-based weather effects (rain / snow).
 * Particles live in local space; the mesh follows the car each frame.
 */
export class Weather {
  constructor(scene) {
    this._scene = scene;
    this._type  = 'none';
    this._mesh  = null;
    this._time  = 0;
  }

  get type() { return this._type; }

  /** @param {'none'|'rain'|'snow'} type */
  setType(type) {
    if (type === this._type) return;
    this._destroy();
    this._type = type;
    if (type === 'rain')  this._createRain();
    if (type === 'snow')  this._createSnow();
  }

  /**
   * Update particle positions (call every frame).
   * @param {number} dt
   * @param {number} carX  World X of car
   * @param {number} carY  World Y of car (height)
   * @param {number} carZ  World Z of car
   */
  update(dt, carX, carY, carZ) {
    if (!this._mesh) return;
    this._time += dt;

    // Move system to follow the car
    this._mesh.position.set(carX, carY, carZ);

    const pos = this._mesh.geometry.attributes.position;
    const N   = pos.count;

    if (this._type === 'rain') {
      for (let i = 0; i < N; i++) {
        let y = pos.getY(i) - 28 * dt;
        let x = pos.getX(i) + 1.5 * dt; // slight wind drift
        if (y < -4) {
          y = 70 + Math.random() * 20;
          pos.setX(i, (Math.random() - 0.5) * 220);
          pos.setZ(i, (Math.random() - 0.5) * 220);
        } else {
          pos.setX(i, x);
        }
        pos.setY(i, y);
      }
    } else if (this._type === 'snow') {
      for (let i = 0; i < N; i++) {
        let y = pos.getY(i) - 3 * dt;
        const sway = Math.sin(this._time * 0.7 + i * 0.08) * 1.8 * dt;
        pos.setX(i, pos.getX(i) + sway);
        if (y < -2) {
          y = 55 + Math.random() * 15;
          pos.setX(i, (Math.random() - 0.5) * 180);
          pos.setZ(i, (Math.random() - 0.5) * 180);
        }
        pos.setY(i, y);
      }
    }

    pos.needsUpdate = true;
  }

  // ── Private ─────────────────────────────────────────────────────────────────

  _createRain() {
    const N   = 9000;
    const pos = new Float32Array(N * 3);
    for (let i = 0; i < N; i++) {
      pos[i * 3]     = (Math.random() - 0.5) * 220;
      pos[i * 3 + 1] = Math.random() * 80;
      pos[i * 3 + 2] = (Math.random() - 0.5) * 220;
    }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));

    const mat = new THREE.PointsMaterial({
      color:          0x99ccee,
      size:           0.14,
      transparent:    true,
      opacity:        0.55,
      sizeAttenuation: true,
      depthWrite:     false,
    });
    this._mesh = new THREE.Points(geo, mat);
    this._scene.add(this._mesh);
  }

  _createSnow() {
    const N   = 3500;
    const pos = new Float32Array(N * 3);
    for (let i = 0; i < N; i++) {
      pos[i * 3]     = (Math.random() - 0.5) * 180;
      pos[i * 3 + 1] = Math.random() * 60;
      pos[i * 3 + 2] = (Math.random() - 0.5) * 180;
    }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));

    const mat = new THREE.PointsMaterial({
      color:           0xffffff,
      size:            0.55,
      transparent:     true,
      opacity:         0.80,
      sizeAttenuation: true,
      depthWrite:      false,
    });
    this._mesh = new THREE.Points(geo, mat);
    this._scene.add(this._mesh);
  }

  _destroy() {
    if (!this._mesh) return;
    this._scene.remove(this._mesh);
    this._mesh.geometry.dispose();
    this._mesh.material.dispose();
    this._mesh = null;
  }
}
